/**
 * ChipCancellationProtocolDialog - Etapa 2: gerar protocolo para um ou mais lotes do mesmo broker.
 *
 * N. protocolo, data de envio e observação são informados uma única vez. O servidor
 * revalida os chips; se algum deixou de ser elegível, o usuário pode retirá-lo e continuar.
 *
 * RELEASE: 2026-10-14 v1.0.0 - Versão inicial.
 */
import { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { InlineNotice } from '@/components/ui/inline-notice';
import { toast } from 'sonner';
import { useChipCancelMutations, type CancelBatch } from '@/hooks/useStockChipCancellations';

const today = () => new Date().toLocaleDateString('en-CA');

export function ChipCancellationProtocolDialog({ batches, open, onOpenChange }: { batches: CancelBatch[]; open: boolean; onOpenChange: (o: boolean) => void }) {
  const [number, setNumber] = useState('');
  const [sentAt, setSentAt] = useState(today());
  const [notes, setNotes] = useState('');
  const [ineligible, setIneligible] = useState<{ count: number; hint?: string } | null>(null);
  const { createProtocol } = useChipCancelMutations();
  const total = batches.reduce((s, b) => s + b.total_items, 0);
  const supplier = batches[0]?.supplier_name ?? '—';

  useEffect(() => { if (open) { setNumber(''); setSentAt(today()); setNotes(''); setIneligible(null); } }, [open]);

  const submit = (removeIneligible = false) => {
    createProtocol.mutate(
      { batchIds: batches.map((b) => b.id), number: number.trim(), sentAt, notes, removeIneligible },
      {
        onSuccess: () => onOpenChange(false),
        onError: (e) => {
          const m = /INELIGIBLE:(\d+)/.exec(e.message);
          if (m) setIneligible({ count: Number(m[1]), hint: (e as Error & { hint?: string }).hint });
          else toast.error(e.message);
        },
      },
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Gerar protocolo</DialogTitle>
          <DialogDescription>
            Broker <strong>{supplier}</strong> · {batches.length} lote(s) · <strong>{total.toLocaleString('pt-BR')}</strong> chip(s) passarão a Solicitado.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label>N. do protocolo *</Label>
            <Input value={number} maxLength={60} autoFocus onChange={(e) => setNumber(e.target.value)} placeholder="Informado pelo broker" />
          </div>
          <div className="space-y-1.5">
            <Label>Data de envio</Label>
            <Input type="date" value={sentAt} max={today()} onChange={(e) => setSentAt(e.target.value)} />
          </div>
          <div className="space-y-1.5">
            <Label>Observação</Label>
            <Textarea value={notes} maxLength={1000} onChange={(e) => setNotes(e.target.value)} />
          </div>
          {ineligible && (
            <InlineNotice variant="warning">
              {ineligible.count} chip(s) deixaram de ser elegíveis (ex.: foram enviados a um prestador).{ineligible.hint ? ` Ex.: ${ineligible.hint}.` : ''} Você pode retirá-los do lote e continuar.
            </InlineNotice>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
          {ineligible ? (
            <Button onClick={() => submit(true)} disabled={createProtocol.isPending}>
              {createProtocol.isPending && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />}Retirar do lote e continuar
            </Button>
          ) : (
            <Button onClick={() => submit(false)} disabled={!number.trim() || createProtocol.isPending}>
              {createProtocol.isPending && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />}Gerar protocolo
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
